
// Collection of helper functions for working with GeoJSON data

export function isFeatureCollection(geojson: unknown): geojson is GeoJSON.FeatureCollection {
   return (geojson as GeoJSON.FeatureCollection)?.type === "FeatureCollection";
}

export function isFeature(geojson: unknown): geojson is GeoJSON.Feature {
   return (geojson as GeoJSON.Feature)?.type === "Feature";
}

export function isFeatureArray(geojson: unknown): geojson is GeoJSON.Feature[] {
   return Array.isArray(geojson) && geojson.every(item => isFeature(item));
}

type unknownGeojson = GeoJSON.FeatureCollection | GeoJSON.Feature | GeoJSON.Feature[] | undefined;

export function transformFeatures<T extends unknownGeojson>(featureCollection: T, transformFn: (feature: GeoJSON.Feature) => GeoJSON.Feature): T {
   if (isFeatureCollection(featureCollection)) {
      return {
         ...featureCollection,
         features: featureCollection.features.map(feature => {
            return transformFn(feature);;
         })
      } as T;
   } else if (isFeature(featureCollection)) {
      return transformFn(featureCollection) as T;
   } else if (isFeatureArray(featureCollection)) {
      return featureCollection.map(feature => {
         return transformFn(feature);;
      }) as T;
   } else {
      return featureCollection;
   }
}

export function extendProperties(featureCollection: unknownGeojson, extension: Partial<GeoJSON.Feature>) {
   transformFeatures(featureCollection, feature => {
      return { ...feature, properties: { ...feature.properties, ...extension.properties } };
   });
}

export function unknownToFeatureCollection(geojson: unknown): GeoJSON.FeatureCollection | undefined {
   if (isFeatureCollection(geojson)) {
      return geojson;
   } else if (isFeature(geojson)) {
      return {
         type: "FeatureCollection",
         features: [geojson]
      };
   } else if (isFeatureArray(geojson)) {
      return {
         type: "FeatureCollection",
         features: geojson
      };
   } else {
      return undefined;
   }
}

export function groupFeatureById(featureCollection: GeoJSON.FeatureCollection, idField: string, includeUnknown?: boolean): Record<string, GeoJSON.Feature[]> {
   return featureCollection.features.reduce((acc, feature) => {
      const id = feature.properties?.[idField];
      if (id) {
         if (!acc[id]) {
            acc[id] = [];
         }
         acc[id].push(feature);
      } else if (includeUnknown) {
         if (!acc["unknown"]) {
            acc["unknown"] = [];
         }
         acc["unknown"].push(feature);
      }
      return acc;
   }, {} as Record<string, GeoJSON.Feature[]>);
}